import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SeoHead from '@/components/SeoHead';
import { Mail, Phone, MapPin, Award, Briefcase, User, GraduationCap } from 'lucide-react';
import everPhoto from '@/assets/ever-quinonez.jpg';

const AboutUs = () => {
    return (
        <div className="min-h-screen bg-white">
            <SeoHead
                title="Quiénes Somos | Servicios Futura"
                description="Conozca a Servicios Futura, la plataforma líder para agentes inmobiliarios en El Salvador. Nuestra misión, visión y valores."
            />
            <div className="container mx-auto px-4 pt-24 pb-16">
                {/* Hero Section */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-[#1A3D5C] mb-6">
                        Innovando el Futuro Inmobiliario
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Servicios Futura nace con un objetivo claro: empoderar a los profesionales inmobiliarios con herramientas tecnológicas de vanguardia, centralizadas y accesibles.
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-12 items-start">
                    {/* Creator Profile */}
                    <div className="lg:col-span-1">
                        <Card className="sticky top-24 border-t-4 border-t-blue-600 shadow-lg">
                            <CardContent className="pt-8 pb-8 flex flex-col items-center text-center">
                                <div className="w-32 h-32 bg-gray-200 rounded-full mb-6 flex items-center justify-center overflow-hidden border-4 border-white shadow-md">
                                    <img
                                        src={everPhoto}
                                        alt="Ever Quiñonez - Fundador & CEO"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-1">Ever Quiñonez</h2>
                                <p className="text-blue-600 font-medium mb-4">Fundador & CEO</p>
                                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                                    Visionario tecnológico con pasión por transformar el sector inmobiliario a través de soluciones digitales eficientes.
                                </p>

                                <div className="w-full space-y-3 text-left">
                                    <div className="flex items-center gap-3 text-gray-600 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                                        <Mail className="w-5 h-5 text-blue-500" />
                                        <span className="text-sm">info@serviciosfutura.com</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-gray-600 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                                        <MapPin className="w-5 h-5 text-blue-500" />
                                        <span className="text-sm">Av. Independencia Sur, Santa Ana, El Salvador</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-12">
                        {/* Mission & Vision */}
                        <section>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <Briefcase className="w-6 h-6 text-blue-600" />
                                Nuestro Objetivo
                            </h3>
                            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                                    Crear un ecosistema digital unificado donde agentes, inmobiliarias y desarrolladores puedan gestionar todo su ciclo de negocio sin fricción.
                                </p>
                                <p className="text-gray-700 leading-relaxed text-lg">
                                    Desde la captación de leads hasta el cierre financiero, Servicios Futura elimina la necesidad de múltiples suscripciones costosas, ofreciendo todo en una sola plataforma robusta y escalable.
                                </p>
                            </div>
                        </section>

                        {/* Experience */}
                        <section>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <Award className="w-6 h-6 text-blue-600" />
                                Trayectoria y Experiencia
                            </h3>
                            <div className="space-y-6">
                                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-blue-500">
                                    <h4 className="font-bold text-lg text-gray-900">Desarrollo de Software Inmobiliario</h4>
                                    <p className="text-gray-600 mt-2">
                                        Más de 5 años de experiencia creando soluciones personalizadas para el sector de bienes raíces, optimizando procesos de venta y alquiler.
                                    </p>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-indigo-500">
                                    <h4 className="font-bold text-lg text-gray-900">Consultoría Tecnológica</h4>
                                    <p className="text-gray-600 mt-2">
                                        Asesoría estratégica para la transformación digital de agencias tradicionales, implementando CRMs y automatización de marketing.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Diplomas / Certifications */}
                        <section>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <GraduationCap className="w-6 h-6 text-blue-600" />
                                Certificaciones
                            </h3>
                            <div className="grid sm:grid-cols-3 gap-4">
                                {/* Diploma 1 */}
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center hover:shadow-md transition-shadow">
                                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
                                        <Award className="w-6 h-6 text-yellow-600" />
                                    </div>
                                    <h5 className="font-bold text-gray-900 text-sm mb-1">Full Stack Developer</h5>
                                    <p className="text-xs text-gray-500">Certificación Profesional</p>
                                </div>

                                {/* Diploma 2 */}
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center hover:shadow-md transition-shadow">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                                        <Award className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h5 className="font-bold text-gray-900 text-sm mb-1">Arquitectura Cloud</h5>
                                    <p className="text-xs text-gray-500">Especialización SaaS</p>
                                </div>

                                {/* Diploma 3 */}
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center hover:shadow-md transition-shadow">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                                        <Award className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h5 className="font-bold text-gray-900 text-sm mb-1">Gestión de Proyectos</h5>
                                    <p className="text-xs text-gray-500">Metodologías Ágiles</p>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutUs;
